import React, { useCallback, useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight, ZoomIn } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Reveal } from "@/components/site/primitives";
import { GALLERY, PHOTO_CREDIT } from "@/content/worldFarmers";

// Bento layout spans (desktop) — asymmetric, editorial.
const SPANS = [
  "sm:col-span-2 sm:row-span-2",
  "",
  "",
  "sm:row-span-2",
  "",
  "sm:col-span-2",
  "",
  "",
  "sm:col-span-2",
  "",
  "",
  "",
];

export const GalleryV2 = () => {
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const total = GALLERY.length;
  const openAt = (i) => { setIndex(i); setOpen(true); };
  const next = useCallback(() => setIndex((i) => (i + 1) % total), [total]);
  const prev = useCallback(() => setIndex((i) => (i - 1 + total) % total), [total]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight") { e.preventDefault(); next(); }
      if (e.key === "ArrowLeft") { e.preventDefault(); prev(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, next, prev]);

  const current = GALLERY[index];

  return (
    <section id="gallery" data-testid="gallery-section" aria-labelledby="gallery-v2-heading" className="wf-grain wf-section bg-[var(--wf-paper)]">
      <div className="wf-container">
        <Reveal className="max-w-2xl">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--wf-maroon)]">
            Documentary photography
          </span>
          <h2 id="gallery-v2-heading" className="mt-3 font-serif text-3xl font-semibold text-[var(--wf-ink)] sm:text-4xl lg:text-5xl">
            From our fields
          </h2>
          <p className="mt-3 text-base leading-7 text-muted-foreground">Real farmers, real harvests. Select any photo to view it larger.</p>
        </Reveal>

        <div data-testid="gallery-grid" className="mt-10 grid auto-rows-[180px] grid-cols-2 gap-3 sm:auto-rows-[200px] sm:grid-cols-4">
          {GALLERY.map((img, i) => (
            <Reveal key={img.src} delay={0.03 * (i % 4)} className={SPANS[i] || ""}>
              <button
                type="button"
                onClick={() => openAt(i)}
                data-testid={`gallery-image-button-${i}`}
                aria-label={`View larger: ${img.alt}`}
                className="group relative block h-full w-full overflow-hidden rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
              >
                <img src={img.src} alt={img.alt} loading="lazy" className="h-full w-full object-cover transition-transform duration-[800ms] ease-out group-hover:scale-110" />
                <span aria-hidden="true" className="absolute inset-0 flex items-center justify-center bg-black/0 transition-colors duration-300 group-hover:bg-black/30">
                  <ZoomIn className="h-6 w-6 text-white opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                </span>
                <span className="absolute left-2 bottom-2 rounded-full bg-black/45 px-2.5 py-0.5 text-[10px] font-medium text-white/90 backdrop-blur-sm opacity-100 sm:opacity-0 sm:group-hover:opacity-100">
                  {PHOTO_CREDIT.label}
                </span>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="gallery-lightbox-dialog" className="max-w-4xl border-[var(--wf-border)] bg-[var(--wf-paper)] p-3 sm:p-4">
          <DialogTitle className="sr-only">Photo {index + 1} of {total}</DialogTitle>
          <DialogDescription className="sr-only">{current?.alt}</DialogDescription>
          <div className="relative overflow-hidden rounded-lg bg-[var(--wf-soil)]">
            <img src={current?.src} alt={current?.alt} className="max-h-[74vh] w-full object-contain" />
            <button type="button" onClick={prev} aria-label="Previous photo" data-testid="gallery-lightbox-prev-button" className="absolute left-2 top-1/2 -translate-y-1/2 inline-flex h-11 w-11 items-center justify-center rounded-full bg-[rgba(255,253,248,0.92)] text-[var(--wf-ink)] shadow-md hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              <ChevronLeft className="h-5 w-5" aria-hidden="true" />
            </button>
            <button type="button" onClick={next} aria-label="Next photo" data-testid="gallery-lightbox-next-button" className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-11 w-11 items-center justify-center rounded-full bg-[rgba(255,253,248,0.92)] text-[var(--wf-ink)] shadow-md hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              <ChevronRight className="h-5 w-5" aria-hidden="true" />
            </button>
            <button type="button" onClick={() => setOpen(false)} aria-label="Close" data-testid="gallery-lightbox-close-button" className="absolute right-2 top-2 inline-flex h-10 w-10 items-center justify-center rounded-full bg-[rgba(255,253,248,0.92)] text-[var(--wf-ink)] shadow-md hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
          <div className="flex items-center justify-between gap-3 px-1 pt-3">
            <p className="text-sm text-muted-foreground">{current?.alt}</p>
            <div className="flex shrink-0 items-center gap-3">
              <a href={PHOTO_CREDIT.href} target="_blank" rel="noopener noreferrer" className="text-xs text-muted-foreground underline decoration-dotted underline-offset-2 hover:text-[var(--wf-maroon)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm">{PHOTO_CREDIT.label}</a>
              <span aria-hidden="true" className="text-xs font-medium text-[var(--wf-maroon)]">{index + 1} / {total}</span>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};
